/**
 * Pure helpers for Sentry environment configuration.
 * Extracted from main.tsx so they can be unit-tested without booting Sentry.
 */

import type { ErrorEvent } from '@sentry/react'

export type SentryEnv = 'production' | 'tests' | 'development' | 'localhost'

/**
 * Resolves the Sentry environment string from the build-time env variable
 * and the runtime hostname.
 *
 * Priority order:
 * 1. localhost hostname (127.0.0.1 or localhost) → 'localhost'
 * 2. appEnv === 'localhost' → 'localhost'
 * 3. appEnv === 'development' → 'development'
 * 4. hostname starts with 'tests.' → 'tests' (prod build on tests host)
 * 5. fallback → 'production'
 */
export function resolveSentryEnv(appEnv: string | undefined, hostname: string): SentryEnv {
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'localhost'
  if (appEnv === 'localhost') return 'localhost'
  if (appEnv === 'development') return 'development'
  if (hostname.startsWith('tests.')) return 'tests'
  return 'production'
}

/**
 * Returns true when Sentry should be active.
 * Always disabled on localhost to prevent dev noise from reaching the project.
 */
export function shouldEnableSentry(env: SentryEnv): boolean {
  return env !== 'localhost'
}

/** Patterns that identify errors originating from injected in-app-browser scripts. */
const INJECTED_SYMBOL_PATTERNS = [
  /Java object is gone/,
  /xbrowser is not defined/,
  /CookieConsentBannerPrototype is not defined/,
  // Meta/Instagram iOS in-app browser injects a native-bridge shim
  // (setupIosCallbackHandler) that reads window.webkit.messageHandlers.
  // Outside a real WKWebView those handlers are absent, so it throws.
  /window\.webkit\.messageHandlers/,
  // Facebook Android in-app browser's own injected TTRC (time-to-render-content)
  // tracking script references a callback it never defines.
  /TTRCCallbacks/,
]

/** Function names from injected in-app-browser bridge scripts. */
const INJECTED_FRAME_FUNCTIONS = ['setupIosCallbackHandler']

/**
 * Minimal shape of the Sentry event that this function requires.
 * Using a structural type so tests can pass plain objects without importing
 * the full ErrorEvent.
 */
type EventLike = Pick<ErrorEvent, 'exception'>

/**
 * Returns true if the event originates from an injected in-app-browser script
 * and should be dropped before sending to Sentry.
 */
export function isInjectedBrowserNoise(event: EventLike): boolean {
  const values = event.exception?.values ?? []
  const frames = values[0]?.stacktrace?.frames ?? []
  if (frames.some((f) => (f.filename ?? '').startsWith('iabjs://'))) return true
  if (frames.some((f) => INJECTED_FRAME_FUNCTIONS.includes(f.function ?? ''))) return true
  const msg = values[0]?.value ?? ''
  return INJECTED_SYMBOL_PATTERNS.some((re) => re.test(msg))
}
